import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import MediaViewer from '../media/MediaViewer';
import { FaList, FaTh } from 'react-icons/fa';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// ─── localStorage helpers for member token ─────────────────────────────────
const MEMBER_TOKEN_KEY = 'memberToken';
const MEMBER_KEY = 'memberInfo';

function getMemberToken() { return localStorage.getItem(MEMBER_TOKEN_KEY); }
function setMemberToken(token) { localStorage.setItem(MEMBER_TOKEN_KEY, token); }
function getMemberInfo() {
  try { return JSON.parse(localStorage.getItem(MEMBER_KEY)); } catch { return null; }
}
function setMemberInfo(info) { localStorage.setItem(MEMBER_KEY, JSON.stringify(info)); }
function clearMemberSession() { localStorage.removeItem(MEMBER_TOKEN_KEY); localStorage.removeItem(MEMBER_KEY); }

function memberHeaders() {
  const token = getMemberToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Shared styles ──────────────────────────────────────────────────────────
const pageStyle = { minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#ffffff', padding: '2rem 1rem' };
const cardStyle = { maxWidth: 420, margin: '0 auto', backgroundColor: '#16213e', borderRadius: 12, padding: '2rem', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' };
const btnPrimary = { width: '100%', padding: '12px', borderRadius: 6, border: 'none', backgroundColor: '#ff6800', color: '#fff', fontWeight: 600, fontSize: '1rem', cursor: 'pointer' };
const btnOutline = { ...btnPrimary, backgroundColor: 'transparent', border: '1px solid #ff6800', color: '#ff6800' };
const btnIconStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.85rem',
  borderRadius: 4,
};

// ─── Stripe PaymentForm (rendered inside <Elements>) ────────────────────────
function PaymentForm({ onSuccess, amount }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message);
      setProcessing(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment did not complete. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <p style={{ color: '#ef4444', marginTop: 8, fontSize: '0.85rem' }}>{error}</p>}
      <button
        type="submit"
        disabled={!stripe || processing}
        style={{ ...btnPrimary, marginTop: 16, opacity: processing ? 0.7 : 1 }}
      >
        {processing ? 'Processing...' : `Pay $${Number(amount).toFixed(2)}`}
      </button>
    </form>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────
export default function PublicEventHistory() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantNum = Number(tenant) || null;

  // Data states
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tenantName, setTenantName] = useState('');

  // Access gate states
  const [requireLogin, setRequireLogin] = useState(false);
  const [accessFee, setAccessFee] = useState(0);
  const [gate, setGate] = useState('loading'); // loading | open | login | setup-password | payment | ready
  const [member, setMember] = useState(getMemberInfo());
  const [mediaViewer, setMediaViewer] = useState(null);
  const [mediaViewMode, setMediaViewMode] = useState(() => {
    try { return localStorage.getItem('os_publichistory_media_view') || 'single'; } catch { return 'single'; }
  });
  const setMediaViewModeAndPersist = (mode) => {
    setMediaViewMode(mode);
    try { localStorage.setItem('os_publichistory_media_view', mode); } catch {}
  };

  // Engagement (likes / comments)
  const [allowLikes, setAllowLikes] = useState(true);
  const [allowComments, setAllowComments] = useState(true);
  const [openComments, setOpenComments] = useState({}); // fileId -> bool
  const [commentsByFile, setCommentsByFile] = useState({}); // fileId -> array
  const [commentDrafts, setCommentDrafts] = useState({}); // fileId -> string
  const [commentBusy, setCommentBusy] = useState({}); // fileId -> bool

  // Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [, setPaymentIntentId] = useState(null);
  const [paymentBusy, setPaymentBusy] = useState(false);

  // Venmo
  const [venmoConfig, setVenmoConfig] = useState(null);

  // ── Step 1: fetch tenant config ─────────────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistoryConfig`);
        if (res.data?.success) {
          setTenantName(res.data.tenantName || '');
          setRequireLogin(res.data.requireLogin);
          setAccessFee(res.data.accessFee || 0);

          if (!res.data.requireLogin) {
            setGate('open');
          } else if (getMemberToken()) {
            // Already have a token — check access
            setGate('ready');
          } else {
            navigate(`/${tenant}/memberlogin`, { replace: true });
            return;
          }
        } else {
          setErr(res.data?.message || 'Event history is not available.');
          setGate('open');
        }
      } catch (e) {
        if (e.response?.status === 403) {
          setErr('This event history is not publicly available.');
        } else {
          setErr('Failed to load event history.');
        }
        setGate('open');
      }
    })();
  }, [tenantNum]);

  // ── Fetch Venmo config for event button ─────────────────────────────────
  useEffect(() => {
    if (!tenantNum) return;
    (async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/venmo/public/${tenantNum}`);
        if (res.data?.success && res.data?.enabled) {
          setVenmoConfig(res.data.venmo);
        }
      } catch {
        // Venmo not configured — that's fine
      }
    })();
  }, [tenantNum]);

  // ── Step 2: fetch history data when gate is open/ready ──────────────────
  const fetchHistory = useCallback(async () => {
    if (!tenantNum) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${baseUrl}/api/events/${tenantNum}/publicHistory`, {
        headers: memberHeaders(),
      });
      if (res.data?.success) {
        setItems(res.data.items || []);
        setTenantName(res.data.tenantName || '');
        if (typeof res.data.allowLikes === 'boolean') setAllowLikes(res.data.allowLikes);
        if (typeof res.data.allowComments === 'boolean') setAllowComments(res.data.allowComments);
      } else {
        setErr(res.data?.message || 'Event history is not available.');
      }
    } catch (e) {
      const status = e.response?.status;
      const data = e.response?.data;
      if (status === 401 && data?.requireLogin) {
        clearMemberSession();
        setMember(null);
        navigate(`/${tenant}/memberlogin`, { replace: true });
        return;
      } else if (status === 402 && data?.requirePayment) {
        setAccessFee(data.fee);
        setGate('payment');
      } else if (status === 403) {
        setErr('This event history is not publicly available.');
      } else {
        setErr('Failed to load event history.');
      }
    } finally {
      setLoading(false);
    }
  }, [tenantNum]);

  useEffect(() => {
    if (gate === 'open' || gate === 'ready') {
      fetchHistory();
    }
  }, [gate, fetchHistory]);

  // ── Payment initiation ─────────────────────────────────────────────────
  useEffect(() => {
    if (gate !== 'payment' || clientSecret) return;
    (async () => {
      setPaymentBusy(true);
      try {
        const res = await axios.post(
          `${baseUrl}/api/members/${tenantNum}/member-pay-access`,
          {},
          { headers: memberHeaders() }
        );
        if (res.data?.alreadyPaid) {
          setGate('ready');
        } else if (res.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
          setPaymentIntentId(res.data.paymentIntentId);
        }
      } catch (e) {
        setErr(e.response?.data?.message || 'Payment setup failed');
        setGate('open');
      } finally {
        setPaymentBusy(false);
      }
    })();
  }, [gate, clientSecret, tenantNum]);

  // ── Payment confirmation ────────────────────────────────────────────────
  const handlePaymentSuccess = async (piId) => {
    try {
      await axios.post(
        `${baseUrl}/api/members/${tenantNum}/member-confirm-payment`,
        { paymentIntentId: piId },
        { headers: memberHeaders() }
      );
      setGate('ready');
    } catch {
      setErr('Payment recorded but confirmation failed. Please refresh.');
    }
  };

  // ── Like toggle ─────────────────────────────────────────────────────────
  // Likes require a member token. If the viewer isn't logged in, we route
  // them to the member login page so they can sign in and come back.
  const handleToggleLike = async (fileId) => {
    if (!getMemberToken()) {
      navigate(`/${tenant}/memberlogin`, { replace: false });
      return;
    }
    // Optimistic update — flip viewerLiked + adjust likeCount immediately,
    // then reconcile with the server response.
    setItems(prev => prev.map(it => {
      if (it.fileId !== fileId) return it;
      const wasLiked = !!it.viewerLiked;
      return {
        ...it,
        viewerLiked: !wasLiked,
        likeCount: Math.max(0, (Number(it.likeCount) || 0) + (wasLiked ? -1 : 1)),
      };
    }));

    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${fileId}/like`,
        {},
        { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setItems(prev => prev.map(it => it.fileId === fileId
          ? { ...it, viewerLiked: !!res.data.liked, likeCount: Number(res.data.likeCount) || 0 }
          : it));
      }
    } catch (e) {
      // Roll back on failure
      setItems(prev => prev.map(it => {
        if (it.fileId !== fileId) return it;
        const wasLiked = !!it.viewerLiked;
        return {
          ...it,
          viewerLiked: !wasLiked,
          likeCount: Math.max(0, (Number(it.likeCount) || 0) + (wasLiked ? -1 : 1)),
        };
      }));
      if (e.response?.status === 401) {
        clearMemberSession();
        setMember(null);
        navigate(`/${tenant}/memberlogin`, { replace: false });
      }
    }
  };

  // ── Comments ────────────────────────────────────────────────────────────
  const fetchComments = async (fileId) => {
    try {
      const res = await axios.get(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${fileId}/comments`
      );
      if (res.data?.success) {
        setCommentsByFile(prev => ({ ...prev, [fileId]: res.data.comments || [] }));
      }
    } catch {
      // best-effort fetch — leave list empty
    }
  };

  const handleToggleComments = async (fileId) => {
    const willOpen = !openComments[fileId];
    setOpenComments(prev => ({ ...prev, [fileId]: willOpen }));
    if (willOpen && !commentsByFile[fileId]) {
      await fetchComments(fileId);
    }
  };

  const handleSubmitComment = async (fileId) => {
    const body = (commentDrafts[fileId] || '').trim();
    if (!body) return;
    if (!getMemberToken()) {
      navigate(`/${tenant}/memberlogin`, { replace: false });
      return;
    }
    setCommentBusy(prev => ({ ...prev, [fileId]: true }));
    try {
      const res = await axios.post(
        `${baseUrl}/api/members/${tenantNum}/public-history/files/${fileId}/comments`,
        { body },
        { headers: memberHeaders() }
      );
      if (res.data?.success && res.data.comment) {
        setCommentsByFile(prev => ({
          ...prev,
          [fileId]: [...(prev[fileId] || []), res.data.comment],
        }));
        setCommentDrafts(prev => ({ ...prev, [fileId]: '' }));
        setItems(prev => prev.map(it => it.fileId === fileId
          ? { ...it, commentCount: (Number(it.commentCount) || 0) + 1 }
          : it));
      }
    } catch (e) {
      if (e.response?.status === 401) {
        clearMemberSession();
        setMember(null);
        navigate(`/${tenant}/memberlogin`, { replace: false });
      }
    } finally {
      setCommentBusy(prev => ({ ...prev, [fileId]: false }));
    }
  };

  const handleDeleteComment = async (fileId, commentId) => {
    if (!getMemberToken()) return;
    try {
      const res = await axios.delete(
        `${baseUrl}/api/members/${tenantNum}/public-history/comments/${commentId}`,
        { headers: memberHeaders() }
      );
      if (res.data?.success) {
        setCommentsByFile(prev => ({
          ...prev,
          [fileId]: (prev[fileId] || []).filter(c => c.id !== commentId),
        }));
        setItems(prev => prev.map(it => it.fileId === fileId
          ? { ...it, commentCount: Math.max(0, (Number(it.commentCount) || 0) - 1) }
          : it));
      }
    } catch {
      // ignore — UI will resync on next page load
    }
  };

  // ── Logout ──────────────────────────────────────────────────────────────
  const handleLogout = () => {
    clearMemberSession();
    setMember(null);
    setItems([]);
    navigate(`/${tenant}/memberlogin`, { replace: true });
  };

  // ── Group items by date ─────────────────────────────────────────────────
  const groups = useMemo(() => {
    const sorted = [...items].sort((a, b) => {
      const da = new Date(a.createdOn || 0);
      const db = new Date(b.createdOn || 0);
      return db - da;
    });
    const by = {};
    for (const it of sorted) {
      const d = new Date(it.createdOn);
      if (!Number.isNaN(d.getTime())) {
        const key = d.toISOString().slice(0, 10);
        (by[key] ||= []).push(it);
      }
    }
    return Object.entries(by).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [items]);

  // ─── RENDER ─────────────────────────────────────────────────────────────

  // Login required — redirect handled by navigate(), show nothing while redirecting
  if (gate === 'login') {
    return null;
  }

  // Password setup — redirect handled by MemberLogin component
  if (gate === 'setup-password') {
    return null;
  }

  // Payment gate
  if (gate === 'payment') {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', marginBottom: '0.5rem' }}>
            {tenantName ? `${tenantName}` : 'Event History'}
          </h2>
          <p style={{ textAlign: 'center', color: '#aaa', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Access to event history requires a one-time payment of <strong style={{ color: '#fff' }}>${Number(accessFee).toFixed(2)}</strong>
          </p>
          {member && (
            <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Signed in as {member.email}
            </p>
          )}
          {paymentBusy && (
            <p style={{ textAlign: 'center', color: '#aaa' }}>Setting up payment...</p>
          )}
          {clientSecret && stripePromise && (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night', variables: { colorPrimary: '#ff6800' } } }}>
              <PaymentForm onSuccess={handlePaymentSuccess} amount={accessFee} />
            </Elements>
          )}
          {!stripePromise && !paymentBusy && (
            <p style={{ textAlign: 'center', color: '#ef4444', fontSize: '0.85rem' }}>
              Payment is not configured. Please contact the event organizer.
            </p>
          )}
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Loading gate check
  if (gate === 'loading') {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
      </div>
    );
  }

  // ── Main history view (gate === 'open' or 'ready') ────────────────────
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header with optional logout */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '2rem', position: 'relative' }}>
          <h2 style={{ textAlign: 'center', color: '#ff6800', margin: 0 }}>
            {tenantName ? `${tenantName} — Event History` : 'Event History'}
          </h2>
          {member && (
            <div style={{ position: 'absolute', right: 0, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => navigate(`/${tenant}/my-tickets`)}
                style={{ ...btnOutline, width: 'auto', padding: '6px 14px', fontSize: '0.8rem' }}
              >
                My Tickets
              </button>
              {requireLogin && (
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{ ...btnOutline, width: 'auto', padding: '6px 14px', fontSize: '0.8rem' }}
                >
                  Sign Out
                </button>
              )}
            </div>
          )}
        </div>

        {loading && (
          <p style={{ textAlign: 'center', color: '#aaa' }}>Loading...</p>
        )}

        {err && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            backgroundColor: '#16213e',
            borderRadius: 8,
            color: '#ff6800',
          }}>
            {err}
          </div>
        )}

        {!loading && !err && items.length === 0 && (
          <p style={{ textAlign: 'center', color: '#aaa' }}>No event history yet.</p>
        )}

        {!loading && !err && items.length > 0 && (
          <div className="media-column-header" style={{ marginBottom: '1rem' }}>
            <span />
            <div className="media-view-toggle" role="group" aria-label="Media view mode">
              <button
                type="button"
                className={`view-toggle-btn ${mediaViewMode === 'single' ? 'active' : ''}`}
                onClick={() => setMediaViewModeAndPersist('single')}
                aria-label="Single column view"
                title="Single column"
              >
                <FaList />
              </button>
              <button
                type="button"
                className={`view-toggle-btn ${mediaViewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setMediaViewModeAndPersist('grid')}
                aria-label="Grid view"
                title="Grid (3 across)"
              >
                <FaTh />
              </button>
            </div>
          </div>
        )}

        {groups.map(([date, arr]) => (
          <div key={date} style={{ marginBottom: '2rem' }}>
            <h4 style={{ color: '#ff6800', marginBottom: '1rem', borderBottom: '1px solid #333', paddingBottom: '0.5rem' }}>
              {new Date(date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </h4>
            <div
              style={
                mediaViewMode === 'grid'
                  ? { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }
                  : { display: 'flex', flexDirection: 'column', gap: '1rem' }
              }
            >
              {arr.map((it) => (
                <div
                  key={`${it.kind}-${it.fileId}`}
                  style={{
                    backgroundColor: '#16213e',
                    borderRadius: 8,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  <div
                    style={{
                      aspectRatio: '4/3',
                      backgroundColor: '#0f0f1a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setMediaViewer({ url: it.url, isVideo: !!it.mimetype?.startsWith('video/') })}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setMediaViewer({ url: it.url, isVideo: !!it.mimetype?.startsWith('video/') })}
                  >
                    {it.mimetype?.startsWith('video/') ? (
                      <video
                        src={it.url}
                        muted
                        playsInline
                        preload="metadata"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <img
                        src={it.url}
                        alt={it.title || 'event image'}
                        loading="lazy"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </div>
                  <div style={{ padding: '0.75rem' }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {it.title}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '0.25rem' }}>
                      {it.kind === 'item' ? `Event Item` : `Event`}
                    </div>

                    {(allowLikes || allowComments) && (
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginTop: '0.75rem',
                        paddingTop: '0.5rem',
                        borderTop: '1px solid #2a2a40',
                      }}>
                        {allowLikes && (
                          <button
                            type="button"
                            onClick={() => handleToggleLike(it.fileId)}
                            title={getMemberToken() ? '' : 'Sign in to like'}
                            style={{
                              ...btnIconStyle,
                              color: it.viewerLiked ? '#ff4d6d' : '#cbd5e1',
                            }}
                          >
                            <span style={{ fontSize: '1rem' }}>{it.viewerLiked ? '♥' : '♡'}</span>
                            <span>{Number(it.likeCount) || 0}</span>
                          </button>
                        )}
                        {allowComments && (
                          <button
                            type="button"
                            onClick={() => handleToggleComments(it.fileId)}
                            style={{ ...btnIconStyle, color: '#cbd5e1' }}
                          >
                            <span style={{ fontSize: '1rem' }}>💬</span>
                            <span>{Number(it.commentCount) || 0}</span>
                          </button>
                        )}
                      </div>
                    )}

                    {allowComments && openComments[it.fileId] && (
                      <CommentsPanel
                        fileId={it.fileId}
                        comments={commentsByFile[it.fileId] || []}
                        draft={commentDrafts[it.fileId] || ''}
                        onDraftChange={(v) => setCommentDrafts(prev => ({ ...prev, [it.fileId]: v }))}
                        onSubmit={() => handleSubmitComment(it.fileId)}
                        onDelete={(commentId) => handleDeleteComment(it.fileId, commentId)}
                        busy={!!commentBusy[it.fileId]}
                        viewerMemberId={member?.id}
                        canPost={!!getMemberToken()}
                        onSignInClick={() => navigate(`/${tenant}/memberlogin`, { replace: false })}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {venmoConfig && (
          <VenmoButton venmo={venmoConfig} />
        )}

        {mediaViewer && (
          <MediaViewer
            src={mediaViewer.url}
            isVideo={mediaViewer.isVideo}
            onClose={() => setMediaViewer(null)}
          />
        )}

        <PoweredByFooter />
      </div>
    </div>
  );
}

function CommentsPanel({
  comments,
  draft,
  onDraftChange,
  onSubmit,
  onDelete,
  busy,
  viewerMemberId,
  canPost,
  onSignInClick,
}) {
  return (
    <div style={{ marginTop: '0.75rem' }}>
      {comments.length === 0 ? (
        <p style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 0.5rem 0' }}>
          No comments yet.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: '0.5rem' }}>
          {comments.map(c => {
            const mine = viewerMemberId && Number(viewerMemberId) === Number(c.memberId);
            return (
              <div key={c.id} style={{
                backgroundColor: '#0f0f1a',
                padding: '6px 8px',
                borderRadius: 6,
                fontSize: '0.85rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <strong style={{ color: '#ff6800', fontSize: '0.8rem' }}>{c.authorName || 'Member'}</strong>
                  {mine && (
                    <button
                      type="button"
                      onClick={() => onDelete(c.id)}
                      style={{
                        background: 'none', border: 'none', color: '#888',
                        cursor: 'pointer', fontSize: '0.7rem', padding: 0,
                      }}
                    >
                      delete
                    </button>
                  )}
                </div>
                <div style={{ color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {c.body}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canPost ? (
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
          style={{ display: 'flex', gap: 6 }}
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="Add a comment…"
            disabled={busy}
            maxLength={2000}
            style={{
              flex: 1,
              padding: '6px 8px',
              borderRadius: 4,
              border: '1px solid #333',
              backgroundColor: '#0f0f1a',
              color: '#fff',
              fontSize: '0.85rem',
            }}
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            style={{
              padding: '6px 10px',
              borderRadius: 4,
              border: 'none',
              backgroundColor: '#ff6800',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy || !draft.trim() ? 0.6 : 1,
            }}
          >
            Post
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={onSignInClick}
          style={{
            background: 'none',
            border: '1px solid #ff6800',
            color: '#ff6800',
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: '0.8rem',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          Sign in to comment
        </button>
      )}
    </div>
  );
}

function VenmoButton({ venmo }) {
  const venmoUrl = `https://venmo.com/${venmo.username.replace(/^@/, '')}` +
    (venmo.defaultAmount ? `?txn=pay&amount=${venmo.defaultAmount}` : '?txn=pay') +
    (venmo.defaultNote ? `&note=${encodeURIComponent(venmo.defaultNote)}` : '');

  return (
    <div style={{
      textAlign: 'center',
      margin: '2rem auto',
      padding: '1.5rem',
      backgroundColor: '#16213e',
      borderRadius: 12,
      maxWidth: 400,
    }}>
      <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '1rem' }}>
        {venmo.defaultNote || `Pay ${venmo.displayName}`}
      </p>
      <a
        href={venmoUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 28px',
          backgroundColor: '#008CFF',
          color: '#fff',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: 8,
          textDecoration: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
          <path d="M19.135 1.755c.8 1.32 1.16 2.68 1.16 4.4 0 5.48-4.68 12.6-8.48 17.6H4.8L2.16 2.395l6.24-.6 1.56 12.52c1.44-2.36 3.24-6.08 3.24-8.64 0-1.64-.28-2.76-.72-3.68l6.655-.245z"/>
        </svg>
        Pay with Venmo
        {venmo.defaultAmount ? ` — $${Number(venmo.defaultAmount).toFixed(2)}` : ''}
      </a>
      <p style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.75rem' }}>
        Opens Venmo to pay {venmo.displayName}
      </p>
    </div>
  );
}

function PoweredByFooter() {
  return (
    <div style={{
      textAlign: 'center',
      marginTop: '3rem',
      paddingTop: '1.5rem',
      borderTop: '1px solid #333',
      color: '#666',
      fontSize: '0.85rem',
    }}>
      Powered by{' '}
      <a
        href="https://on-screen.app"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#ff6800', textDecoration: 'none' }}
      >
        On-Screen
      </a>
    </div>
  );
}
